import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { ChartType } from '../types/visualization';

export const useReportWidgets = (enabled: boolean = true) => {
  const queryClient = useQueryClient();

  // Fetch widgets
  const { data: widgetsData, isLoading, error } = useQuery({
    queryKey: ['reportWidgets'],
    queryFn: () => apiService.getReportWidgets(),
    enabled,
  });

  const widgets = widgetsData?.widgets || [];

  // Create widget mutation
  const createWidgetMutation = useMutation({
    mutationFn: (widget: { 
      name: string; 
      nl_query: string; 
      sql_query: string; 
      visualization_type: ChartType;
      config?: any;
      metadata?: any;
    }) => apiService.createReportWidget(widget),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportWidgets'] });
    },
  });

  // Delete widget mutation
  const deleteWidgetMutation = useMutation({
    mutationFn: (widgetId: number) => apiService.deleteReportWidget(widgetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportWidgets'] });
    },
  });

  // Helper functions
  const createWidget = async (widget: Parameters<typeof createWidgetMutation.mutate>[0]) => {
    return createWidgetMutation.mutate(widget);
  };

  const deleteWidget = (widgetId: number) => {
    if (window.confirm('Are you sure you want to delete this widget?')) {
      deleteWidgetMutation.mutate(widgetId);
    }
  };

  return {
    widgets,
    isLoading,
    error,
    createWidget,
    deleteWidget,
    isCreating: createWidgetMutation.isPending,
    isDeleting: deleteWidgetMutation.isPending,
  };
};
